<script setup lang="ts">
import { computed, useSlots } from 'vue'
import PrimeButton from 'primevue/button'

type ButtonSeverity =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'warn'
  | 'help'
  | 'danger'
  | 'contrast'

type ButtonVariant = 'filled' | 'outlined' | 'text'
type ButtonSize = 'small' | 'large'

interface Props {
  label?: string
  severity?: ButtonSeverity
  variant?: ButtonVariant
  loading?: boolean
  size?: ButtonSize
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  icon?: string
  iconPos?: 'left' | 'right'
  buttonClass?: string
  pt?: object
  ptOptions?: object
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  severity: 'primary',
  variant: 'filled',
  loading: false,
  size: 'small',
  disabled: false,
  type: 'button',
  iconPos: 'left',
  buttonClass:
    '!montserrat w-full !rounded-lg flex items-center justify-center gap-2 !px-6 !min-w-[120px]',
})

defineOptions({ inheritAttrs: false })

const slots = useSlots()

const isDisabled = computed(() => props.loading || props.disabled)

const hasCustomIcon = computed(() => !!slots.icon)

const resolvedVariant = computed(() => {
  if (props.severity === 'secondary') return 'outlined'
  return props.variant
})
</script>

<template>
  <PrimeButton
    :label="!hasCustomIcon ? props.label : undefined"
    :severity="props.severity"
    :variant="resolvedVariant"
    :loading="props.loading"
    :size="props.size"
    :disabled="isDisabled"
    :type="props.type"
    :icon="!hasCustomIcon ? props.icon : undefined"
    :iconPos="props.iconPos"
    :pt="props.pt"
    :ptOptions="props.ptOptions"
    v-bind="$attrs"
    :class="[
      '!border-solid !border !border-[color-mix(in_srgb,var(--p-surface-0)_42%,transparent)] !border-t-1 !border-t-[color-mix(in_srgb,var(--p-surface-0)_52%,transparent)] !border-l-1 !border-l-[color-mix(in_srgb,var(--p-surface-0)_82%,transparent)] !border-r-[color-mix(in_srgb,var(--p-surface-0)_28%,transparent)] !border-b-[color-mix(in_srgb,var(--p-surface-0)_22%,transparent)] bg-[color-mix(in_srgb,var(--p-surface-0)_30%,transparent)] backdrop-blur-md backdrop-saturate-125 shadow-sm transition-all duration-200 hover:!border-[color-mix(in_srgb,var(--p-primary-color)_2%,transparent)] hover:shadow-[0_3px_6px_color-mix(in_srgb,var(--p-primary-color)_16%,transparent)] active:translate-y-px disabled:!cursor-not-allowed disabled:opacity-55 disabled:shadow-none dark:!border-t-[color-mix(in_srgb,var(--p-primary-color)_20%,transparent)] dark:!border-r-[color-mix(in_srgb,var(--p-primary-color)_18%,transparent)] dark:!border-b-[color-mix(in_srgb,var(--p-primary-color)_18%,transparent)] dark:!border-l-[color-mix(in_srgb,var(--p-primary-color)_30%,transparent)] dark:bg-[color-mix(in_srgb,var(--p-surface-900)_42%,transparent)] dark:shadow-[inset_1px_1px_0_color-mix(in_srgb,var(--p-primary-color)_24%,transparent),0_8px_20px_rgb(2_6_23_/_22%)]',

      props.severity === 'primary' &&
        '!bg-[color-mix(in_srgb,var(--p-primary-color)_82%,transparent)] dark:bg-[color-mix(in_srgb,var(--p-primary-900)_82%,transparent)]! !text-white relative overflow-hidden after:absolute shadow-sm hover:shadow-[0_3px_6px_color-mix(in_srgb,var(--p-primary-color)_16%,transparent)] after:inset-y-0 after:left-[-100%] after:w-[60%] after:bg-white/25 after:-skew-x-12 hover:after:left-[150%] after:transition-all after:duration-500 dark:!bg-[color-mix(in_srgb,var(--p-primary-color)_62%,var(--p-surface-900))]',

      props.severity === 'secondary' &&
        '!bg-[color-mix(in_srgb,var(--p-primary-color)_8%,transparent)] !text-[var(--p-primary-color)] hover:!bg-[color-mix(in_srgb,var(--p-primary-color)_20%,transparent)] transition-all duration-200 shadow-sm hover:shadow-[0_3px_6px_color-mix(in_srgb,var(--p-primary-color)_16%,transparent)]',

      props.severity === 'tertiary' &&
        '!bg-[color-mix(in_srgb,var(--p-surface-0)_30%,transparent)] !text-gray-600 dark:!text-gray-300 hover:!bg-gray-100/70 dark:hover:!bg-white/10',

      props.severity === 'quaternary' && '!bg-[color-mix(in_srgb,var(--p-primary-color)_30%,transparent)] !border-transparent',

      props.buttonClass,
    ]"
  >
    <!-- Si hay icon custom -->
    <template v-if="hasCustomIcon">
      <span v-if="props.iconPos === 'left'" class="flex items-center">
        <slot name="icon" />
      </span>

      <span v-if="props.label">
        {{ props.label }}
      </span>

      <span v-if="props.iconPos === 'right'" class="flex items-center">
        <slot name="icon" />
      </span>
    </template>
  </PrimeButton>
</template>

<style scoped>
:deep(.p-button) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Montserrat';
  /* margin-top: 15px; */
}

/* :deep(.p-button:hover) {
  background-color: rgba(var(--p-primary-color-rgb), 0.1) !important;
  border-color: var(--p-primary-color) !important;
  opacity: 1 !important;
}  */
</style>
